import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from 'lib/utils'
import * as React from 'react'

const inputVariants = cva(
  'flex min-w-0 text-base transition-[color,box-shadow] outline-none file:inline-flex file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
  {
    variants: {
      variant: {
        default:
          'h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 shadow-xs selection:bg-primary selection:text-primary-foreground file:h-7 file:text-foreground placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:bg-input/30 dark:aria-invalid:ring-destructive/40',
        primary:
          'h-[52px] w-full rounded-[10px] border border-[#d0d5dd] bg-white px-[18px] text-base font-medium shadow-[0px_3px_6px_0px_rgba(0,0,0,0.06)] placeholder:font-medium placeholder:text-[#98a2b3] focus:border-[#6161ff] focus:ring-2 focus:ring-[#6161ff]/20 md:text-sm',
        onboarding:
          'w-full rounded-xl border border-transparent bg-[#f5f5f7] px-5 py-4 text-base font-normal text-[#1a1a2e] transition-[border-color] outline-none placeholder:font-normal placeholder:text-[#98a2b3] focus:border-[color:var(--acid)]',
        // T3 design system. Spec: playbook/design-system/components.md
        // ("Composer Row"). Single 1px line at rest, 1px acid on focus, no
        // soft halo. The `!` on focus:border-primary is required to beat the
        // layer-less .border-line-bright shim (see foundations.md
        // "Shim-class override gotcha").
        dark: 'h-[48px] w-full rounded-[10px] border border-line-bright bg-bg-3 px-4 text-[15px] font-normal text-paper transition-[border-color] placeholder:text-paper-faint focus:border-primary! aria-invalid:border-[color:var(--alert-error-border)] md:text-[15px]',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

function Input({
  className,
  variant,
  type,
  ...props
}: React.ComponentProps<'input'> & VariantProps<typeof inputVariants>) {
  return (
    <input
      type={type}
      data-slot="input"
      className={cn(inputVariants({ variant, className }))}
      {...props}
    />
  )
}

export { Input }
